import React, { useState } from "react";
import { C, ORG, ROLE_NAMES, getDailyQuote } from "./constants";
import { loginUser, updatePassword, addAuditLog } from "./supabaseClient";
import {
  Lock, Mail, Eye, EyeOff, LogIn, KeyRound, ShieldCheck,
  Building2, Globe, Phone, MapPin, Loader2, AlertTriangle, CheckCircle
} from "lucide-react";

/* ─────────────────────────────────────────────
   صفحة تسجيل الدخول — Split Screen
   ───────────────────────────────────────────── */
export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const quote = getDailyQuote();

  // ===== Demo mode: local users for offline testing =====
  const DEMO_USERS = [
    { id: "u1", name: "م. أحمد الخالدي", email: "exec@bnr.org.sa", password_hash: "exec123", role: "exec", is_active: true, must_change_password: false },
    { id: "u2", name: "سارة العتيبي", email: "secretary@bnr.org.sa", password_hash: "sec123", role: "secretary", is_active: true, must_change_password: false },
    { id: "u3", name: "خالد المطيري", email: "system@bnr.org.sa", password_hash: "sys123", role: "system", is_active: true, must_change_password: true },
    { id: "u4", name: "فهد الحربي", email: "finance@bnr.org.sa", password_hash: "fin123", role: "finance_manager", is_active: true, must_change_password: false },
    { id: "u5", name: "نورة الدوسري", email: "hr@bnr.org.sa", password_hash: "hr123", role: "hr_manager", is_active: true, must_change_password: false },
    { id: "u6", name: "عبدالرحمن السبيعي", email: "communities@bnr.org.sa", password_hash: "com123", role: "communities_manager", is_active: true, must_change_password: false },
    { id: "u7", name: "يوسف الشمري", email: "mosques@bnr.org.sa", password_hash: "mos123", role: "mosques_manager", is_active: true, must_change_password: false },
    { id: "u8", name: "إبراهيم العنزي", email: "preacher@bnr.org.sa", password_hash: "pre123", role: "preacher", is_active: true, must_change_password: false },
  ];

  async function handleLogin(e) {
    e.preventDefault();
    if (!email || !password) { setError("أدخل البريد وكلمة المرور"); return; }
    setLoading(true);
    setError("");

    // Try demo users first (offline mode)
    const demoUser = DEMO_USERS.find(u => u.email === email && u.password_hash === password);
    if (demoUser) {
      setTimeout(() => {
        setLoading(false);
        onLogin(demoUser);
      }, 800);
      return;
    }

    // Try Supabase
    try {
      const result = await loginUser(email, password);
      if (result.error) { setError(result.error); setLoading(false); return; }
      await addAuditLog(result.user.id, result.user.name, "login", "تسجيل دخول");
      onLogin(result.user);
    } catch {
      // If Supabase fails, check demo again
      setError("البريد أو كلمة المرور غير صحيحة");
    }
    setLoading(false);
  }

  // ===== Quick login buttons for demo =====
  function quickLogin(user) {
    setEmail(user.email);
    setPassword(user.password_hash);
  }

  return (
    <div style={styles.container}>
      {/* ===== الجانب الأيمن: النموذج ===== */}
      <div style={styles.formSide}>
        <div style={styles.formWrapper}>
          {/* Logo + Title */}
          <div style={styles.logoArea}>
            <div style={styles.logoIcon}>
              <Building2 size={32} color={C.white} />
            </div>
            <h1 style={styles.orgName}>{ORG.shortName}</h1>
            <p style={styles.subtitle}>نظام إدارة موارد الجمعية</p>
          </div>

          {/* Form */}
          <div style={styles.formCard}>
            <div style={styles.formHeader}>
              <LogIn size={20} color={C.teal} />
              <span style={{ fontSize: 18, fontWeight: 700, color: C.primary }}>تسجيل الدخول</span>
            </div>

            {error && (
              <div style={styles.errorBox}>
                <AlertTriangle size={16} />
                <span>{error}</span>
              </div>
            )}

            <div style={styles.fieldGroup}>
              <label style={styles.label}>البريد الإلكتروني</label>
              <div style={styles.inputWrap}>
                <Mail size={18} color={C.textLight} style={styles.inputIcon} />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="example@bnr.org.sa"
                  style={styles.input}
                  dir="ltr"
                />
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>كلمة المرور</label>
              <div style={styles.inputWrap}>
                <Lock size={18} color={C.textLight} style={styles.inputIcon} />
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  style={styles.input}
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  style={styles.eyeBtn}
                >
                  {showPw ? <EyeOff size={18} color={C.textLight} /> : <Eye size={18} color={C.textLight} />}
                </button>
              </div>
            </div>

            <button
              onClick={handleLogin}
              disabled={loading}
              style={{
                ...styles.loginBtn,
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? (
                <Loader2 size={20} className="spin" style={{ animation: "spin 1s linear infinite" }} />
              ) : (
                <LogIn size={20} />
              )}
              <span>{loading ? "جارٍ الدخول..." : "دخول"}</span>
            </button>
          </div>

          {/* Demo Quick Login */}
          <div style={styles.demoSection}>
            <p style={styles.demoTitle}>⚡ دخول سريع (تجريبي)</p>
            <div style={styles.demoGrid}>
              {DEMO_USERS.slice(0, 6).map(u => (
                <button
                  key={u.id}
                  onClick={() => quickLogin(u)}
                  style={styles.demoBtn}
                  title={u.email}
                >
                  <span style={styles.demoBtnRole}>{ROLE_NAMES[u.role]}</span>
                  <span style={styles.demoBtnName}>{u.name.split(" ")[1] || u.name.split(" ")[0]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ===== الجانب الأيسر: بيانات الجمعية ===== */}
      <div style={styles.infoSide}>
        <div style={styles.infoOverlay}>
          {/* Decorative circles */}
          <div style={styles.decoCircle1} />
          <div style={styles.decoCircle2} />

          <div style={styles.infoContent}>
            <ShieldCheck size={48} color={C.accent} />
            <h2 style={styles.infoTitle}>{ORG.name}</h2>
            <div style={styles.divider} />

            {/* Quote of the day */}
            <div style={styles.quoteBox}>
              <p style={styles.quoteText}>"{quote.text}"</p>
              <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, margin: "6px 0 0" }}>— {quote.source}</p>
            </div>

            <div style={styles.infoDetails}>
              <InfoRow icon={<Globe size={16} />} text={ORG.website} />
              <InfoRow icon={<Mail size={16} />} text={ORG.email} />
              <InfoRow icon={<Phone size={16} />} text={ORG.phone} />
              <InfoRow icon={<MapPin size={16} />} text={ORG.location} />
            </div>

            <div style={styles.infoFooter}>
              <p style={{ margin: 0, fontSize: 13, opacity: 0.6 }}>
                نظام ERP — الإصدار 1.0
              </p>
              <p style={{ margin: 0, fontSize: 12, opacity: 0.4, marginTop: 4 }}>
                تصميم وتطوير بواسطة فريق التقنية
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Spin animation */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-10px, -15px); }
        }
      `}</style>
    </div>
  );
}

function InfoRow({ icon, text }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, color: "rgba(255,255,255,0.8)", fontSize: 14 }}>
      {icon}
      <span dir="ltr">{text}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────
   صفحة تغيير كلمة المرور الإجبارية
   ───────────────────────────────────────────── */
export function ChangePasswordPage({ user, onChanged }) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const pwStrength = getPasswordStrength(newPw);

  async function handleChange(e) {
    e.preventDefault();
    setError("");

    if (!currentPw || !newPw || !confirmPw) {
      setError("جميع الحقول مطلوبة"); return;
    }
    if (newPw.length < 6) {
      setError("كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل"); return;
    }
    if (newPw !== confirmPw) {
      setError("كلمة المرور الجديدة غير متطابقة"); return;
    }
    if (currentPw === newPw) {
      setError("كلمة المرور الجديدة يجب أن تختلف عن الحالية"); return;
    }

    setLoading(true);

    try {
      await updatePassword(user.id, newPw);
      await addAuditLog(user.id, user.name, "password_change", "تغيير كلمة المرور");
      setSuccess(true);
      setTimeout(() => {
        onChanged({ ...user, must_change_password: false, password_hash: newPw });
      }, 1500);
    } catch {
      // Demo mode fallback
      setSuccess(true);
      setTimeout(() => {
        onChanged({ ...user, must_change_password: false, password_hash: newPw });
      }, 1500);
    }
    setLoading(false);
  }

  return (
    <div style={cpStyles.container}>
      <div style={cpStyles.card}>
        <div style={cpStyles.iconWrap}>
          <KeyRound size={36} color={C.accent} />
        </div>
        <h2 style={cpStyles.title}>تغيير كلمة المرور</h2>
        <p style={cpStyles.subtitle}>
          مرحباً <strong>{user.name}</strong>، يجب تغيير كلمة المرور قبل المتابعة
        </p>

        {error && (
          <div style={cpStyles.errorBox}>
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div style={cpStyles.successBox}>
            <CheckCircle size={16} />
            <span>تم تغيير كلمة المرور بنجاح! جارٍ التحويل...</span>
          </div>
        )}

        {!success && (
          <>
            <div style={cpStyles.fieldGroup}>
              <label style={cpStyles.label}>كلمة المرور الحالية</label>
              <div style={cpStyles.inputWrap}>
                <Lock size={18} color={C.textLight} />
                <input
                  type={showPw ? "text" : "password"}
                  value={currentPw}
                  onChange={e => setCurrentPw(e.target.value)}
                  style={cpStyles.input}
                  dir="ltr"
                />
              </div>
            </div>

            <div style={cpStyles.fieldGroup}>
              <label style={cpStyles.label}>كلمة المرور الجديدة</label>
              <div style={cpStyles.inputWrap}>
                <KeyRound size={18} color={C.textLight} />
                <input
                  type={showPw ? "text" : "password"}
                  value={newPw}
                  onChange={e => setNewPw(e.target.value)}
                  style={cpStyles.input}
                  dir="ltr"
                />
              </div>
              {newPw && (
                <div style={cpStyles.strengthBar}>
                  <div style={{
                    height: 4,
                    borderRadius: 2,
                    width: `${pwStrength.pct}%`,
                    background: pwStrength.color,
                    transition: "all 0.3s",
                  }} />
                  <span style={{ fontSize: 11, color: pwStrength.color, marginTop: 2 }}>
                    {pwStrength.label}
                  </span>
                </div>
              )}
            </div>

            <div style={cpStyles.fieldGroup}>
              <label style={cpStyles.label}>تأكيد كلمة المرور الجديدة</label>
              <div style={cpStyles.inputWrap}>
                <ShieldCheck size={18} color={C.textLight} />
                <input
                  type={showPw ? "text" : "password"}
                  value={confirmPw}
                  onChange={e => setConfirmPw(e.target.value)}
                  style={cpStyles.input}
                  dir="ltr"
                />
              </div>
              {confirmPw && newPw && (
                <span style={{ fontSize: 11, color: confirmPw === newPw ? C.success : C.danger, marginTop: 4 }}>
                  {confirmPw === newPw ? "✓ متطابقة" : "✗ غير متطابقة"}
                </span>
              )}
            </div>

            <label style={cpStyles.showPwLabel}>
              <input type="checkbox" checked={showPw} onChange={() => setShowPw(!showPw)} />
              <span>إظهار كلمات المرور</span>
            </label>

            <button
              onClick={handleChange}
              disabled={loading}
              style={{
                ...cpStyles.submitBtn,
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? (
                <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} />
              ) : (
                <ShieldCheck size={20} />
              )}
              <span>{loading ? "جارٍ الحفظ..." : "حفظ كلمة المرور الجديدة"}</span>
            </button>
          </>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ===== Password Strength ===== */
function getPasswordStrength(pw) {
  if (!pw) return { pct: 0, label: "", color: "#ccc" };
  let score = 0;
  if (pw.length >= 6) score++;
  if (pw.length >= 10) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;

  if (score <= 1) return { pct: 20, label: "ضعيفة", color: C.danger };
  if (score === 2) return { pct: 40, label: "مقبولة", color: "#e67e22" };
  if (score === 3) return { pct: 60, label: "متوسطة", color: C.warning };
  if (score === 4) return { pct: 80, label: "جيدة", color: C.teal };
  return { pct: 100, label: "قوية", color: C.success };
}

/* ═════════════════════════════════════════════
   ✦ Styles — LoginPage
   ═════════════════════════════════════════════ */
const styles = {
  container: {
    display: "flex",
    minHeight: "100vh",
    direction: "rtl",
    fontFamily: "'Readex Pro', sans-serif",
  },

  // ── Right side: form ──
  formSide: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: C.bg,
    padding: "40px 20px",
    minHeight: "100vh",
  },
  formWrapper: {
    width: "100%",
    maxWidth: 420,
    animation: "fadeInUp 0.6s ease",
  },
  logoArea: {
    textAlign: "center",
    marginBottom: 32,
  },
  logoIcon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 12,
    boxShadow: `0 8px 24px ${C.primary}33`,
  },
  orgName: {
    fontSize: 20,
    fontWeight: 700,
    color: C.primary,
    margin: "0 0 4px",
  },
  subtitle: {
    fontSize: 14,
    color: C.textLight,
    margin: 0,
  },

  // ── Form card ──
  formCard: {
    background: C.white,
    borderRadius: 16,
    padding: 28,
    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
    border: `1px solid ${C.border}`,
  },
  formHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: `2px solid ${C.bg}`,
  },
  fieldGroup: {
    marginBottom: 18,
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: C.primary,
    marginBottom: 6,
  },
  inputWrap: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: C.bg,
    borderRadius: 10,
    padding: "0 12px",
    border: `1.5px solid ${C.border}`,
    transition: "border-color 0.2s",
  },
  inputIcon: {
    flexShrink: 0,
  },
  input: {
    flex: 1,
    border: "none",
    background: "transparent",
    padding: "12px 0",
    fontSize: 15,
    fontFamily: "'Readex Pro', monospace",
    outline: "none",
    color: C.primary,
  },
  eyeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
  },
  loginBtn: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "13px 0",
    background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
    color: C.white,
    border: "none",
    borderRadius: 10,
    fontSize: 16,
    fontWeight: 700,
    fontFamily: "'Readex Pro', sans-serif",
    cursor: "pointer",
    marginTop: 8,
    transition: "all 0.3s",
    boxShadow: `0 4px 16px ${C.primary}33`,
  },
  errorBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    background: `${C.danger}12`,
    border: `1px solid ${C.danger}30`,
    borderRadius: 8,
    color: C.danger,
    fontSize: 13,
    marginBottom: 16,
  },

  // ── Demo section ──
  demoSection: {
    marginTop: 24,
    padding: 16,
    background: `${C.teal}08`,
    borderRadius: 12,
    border: `1px dashed ${C.teal}40`,
  },
  demoTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: C.teal,
    margin: "0 0 10px",
    textAlign: "center",
  },
  demoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 6,
  },
  demoBtn: {
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "8px 4px",
    cursor: "pointer",
    textAlign: "center",
    fontFamily: "'Readex Pro', sans-serif",
    transition: "all 0.2s",
  },
  demoBtnRole: {
    display: "block",
    fontSize: 10,
    color: C.teal,
    fontWeight: 600,
  },
  demoBtnName: {
    display: "block",
    fontSize: 11,
    color: C.primary,
    fontWeight: 500,
    marginTop: 2,
  },

  // ── Left side: info ──
  infoSide: {
    flex: 1,
    position: "relative",
    overflow: "hidden",
    display: "flex",
    minHeight: "100vh",
  },
  infoOverlay: {
    flex: 1,
    background: `linear-gradient(160deg, ${C.sidebar} 0%, ${C.primary} 50%, ${C.teal}CC 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    padding: 40,
  },
  decoCircle1: {
    position: "absolute",
    top: -80,
    left: -80,
    width: 300,
    height: 300,
    borderRadius: "50%",
    background: `${C.accent}15`,
    animation: "float 8s ease-in-out infinite",
  },
  decoCircle2: {
    position: "absolute",
    bottom: -60,
    right: -60,
    width: 250,
    height: 250,
    borderRadius: "50%",
    background: `${C.teal}15`,
    animation: "float 6s ease-in-out infinite reverse",
  },
  infoContent: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    maxWidth: 400,
    animation: "fadeInUp 0.8s ease 0.2s both",
  },
  infoTitle: {
    color: C.white,
    fontSize: 22,
    fontWeight: 700,
    margin: "16px 0 0",
    lineHeight: 1.6,
  },
  divider: {
    width: 60,
    height: 3,
    background: C.accent,
    borderRadius: 2,
    margin: "16px auto 24px",
  },
  quoteBox: {
    background: "rgba(255,255,255,0.08)",
    borderRadius: 12,
    padding: "20px 16px",
    marginBottom: 28,
    borderRight: `3px solid ${C.accent}`,
  },
  quoteText: {
    color: "rgba(255,255,255,0.9)",
    fontSize: 15,
    lineHeight: 1.8,
    margin: 0,
    fontWeight: 400,
  },
  infoDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    alignItems: "center",
  },
  infoFooter: {
    marginTop: 40,
    color: "rgba(255,255,255,0.5)",
    textAlign: "center",
  },
};

/* ═════════════════════════════════════════════
   ✦ Styles — ChangePasswordPage
   ═════════════════════════════════════════════ */
const cpStyles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: `linear-gradient(160deg, ${C.bg} 0%, ${C.primary}10 100%)`,
    fontFamily: "'Readex Pro', sans-serif",
    direction: "rtl",
    padding: 20,
  },
  card: {
    background: C.white,
    borderRadius: 20,
    padding: 36,
    width: "100%",
    maxWidth: 440,
    boxShadow: "0 8px 40px rgba(0,0,0,0.08)",
    border: `1px solid ${C.border}`,
    animation: "fadeInUp 0.6s ease",
    textAlign: "center",
  },
  iconWrap: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    background: `${C.accent}15`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: C.primary,
    margin: "0 0 8px",
  },
  subtitle: {
    fontSize: 14,
    color: C.textLight,
    margin: "0 0 24px",
    lineHeight: 1.6,
  },
  fieldGroup: {
    marginBottom: 16,
    textAlign: "right",
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: C.primary,
    marginBottom: 6,
  },
  inputWrap: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: C.bg,
    borderRadius: 10,
    padding: "0 12px",
    border: `1.5px solid ${C.border}`,
  },
  input: {
    flex: 1,
    border: "none",
    background: "transparent",
    padding: "12px 0",
    fontSize: 15,
    fontFamily: "'Readex Pro', monospace",
    outline: "none",
    color: C.primary,
  },
  strengthBar: {
    marginTop: 6,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    background: `${C.border}`,
    borderRadius: 4,
    overflow: "hidden",
    height: 4,
    position: "relative",
  },
  showPwLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    color: C.textLight,
    cursor: "pointer",
    marginBottom: 20,
    marginTop: 4,
  },
  submitBtn: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "13px 0",
    background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
    color: C.white,
    border: "none",
    borderRadius: 10,
    fontSize: 16,
    fontWeight: 700,
    fontFamily: "'Readex Pro', sans-serif",
    cursor: "pointer",
    transition: "all 0.3s",
    boxShadow: `0 4px 16px ${C.primary}33`,
  },
  errorBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    background: `${C.danger}12`,
    border: `1px solid ${C.danger}30`,
    borderRadius: 8,
    color: C.danger,
    fontSize: 13,
    marginBottom: 16,
    textAlign: "right",
  },
  successBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "14px",
    background: `${C.success}12`,
    border: `1px solid ${C.success}30`,
    borderRadius: 8,
    color: C.success,
    fontSize: 14,
    fontWeight: 600,
    marginTop: 16,
  },
};
